import {
  defaultCountries,
  getActiveFormattingMask,
  guessCountryByPartialPhoneNumber,
  parseCountry,
} from 'react-international-phone';

const MASK_SYMBOL = '.';
const DEFAULT_MASK = '............';

const onlyDigits = (value: string): string => value.replace(/\D/g, '');

const getCountryByIso = (iso2: string) => {
  const country = defaultCountries.find((data) => parseCountry(data).iso2 === iso2);
  return country ? parseCountry(country) : undefined;
};

const applyMask = (digits: string, mask: string): string => {
  let result = '';
  let index = 0;

  for (const char of mask) {
    if (index >= digits.length) break;
    result += char === MASK_SYMBOL ? digits[index++] : char;
  }

  return result;
};

/**
 * Splits a stored number into its dial code and its masked national part, the
 * way `react-international-phone` does for the `FormPhone` input.
 */
const parsePhoneNumber = (value: string, defaultCountry: string) => {
  let phone = value;

  // Numbers without a prefix are treated as national for the default country,
  // exactly like the input does when `disableDialCodeAndPrefix` is set.
  if (!phone.startsWith('+')) {
    const country = getCountryByIso(defaultCountry);
    phone = `+${country?.dialCode ?? ''}${onlyDigits(phone)}`;
  }

  const country =
    guessCountryByPartialPhoneNumber({ phone }).country ?? getCountryByIso(defaultCountry);
  if (!country) return undefined;

  const mask = getActiveFormattingMask({ phone, country, defaultMask: DEFAULT_MASK });

  const allDigits = onlyDigits(phone);
  const dialDigits = onlyDigits(country.dialCode);
  const national = allDigits.startsWith(dialDigits)
    ? allDigits.slice(dialDigits.length)
    : allDigits;

  return { dialCode: dialDigits, national: applyMask(national, mask) };
};

/**
 * Formats a stored phone number for display so it matches exactly what the
 * `FormPhone` input shows: the national number without the dial code/prefix
 * (e.g. `+12025550123` -> `(202) 555-0123`).
 *
 * Mirrors `react-international-phone`'s formatting (mask selection + dial code
 * stripping) used by the input with `disableDialCodeAndPrefix`.
 */
export const formatPhoneNumber = (value?: string | null, defaultCountry = 'us'): string => {
  if (!value) return '';

  const phone = String(value).trim();
  if (!phone) return '';

  const parsed = parsePhoneNumber(phone, defaultCountry);
  return parsed ? parsed.national : phone;
};

/**
 * Same as `formatPhoneNumber` but keeps the country dial code in front
 * (e.g. `+12025550123` -> `+1 (202) 555-0123`), for numbers someone may have
 * to dial from another country — an emergency contact, say.
 */
export const formatPhoneNumberWithCode = (value?: string | null, defaultCountry = 'us'): string => {
  if (!value) return '';

  const phone = String(value).trim();
  if (!phone) return '';

  const parsed = parsePhoneNumber(phone, defaultCountry);
  if (!parsed) return phone;

  return parsed.national ? `+${parsed.dialCode} ${parsed.national}` : `+${parsed.dialCode}`;
};
